import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IoIosCloud } from "react-icons/io";
import { FaCrown } from "react-icons/fa";
import cloudApi from "../../../services/cloudApi";
import { useAuth } from "../../../contexts/AuthContext";
const AdminView = () => {
  const { logout, enterViewAs, exitViewAs, viewAsUser } = useAuth();
  const navigate = useNavigate();

  // View As state
  const [viewAsSearch, setViewAsSearch] = useState("");
  const [viewAsResults, setViewAsResults] = useState([]);
  const [viewAsSearching, setViewAsSearching] = useState(false);

  useEffect(() => {
    if (viewAsSearch.length < 2) {
      setViewAsResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setViewAsSearching(true);
      try {
        const data = await cloudApi.searchAdminUsers(viewAsSearch);
        setViewAsResults(data.users || []);
      } catch {
        setViewAsResults([]);
      } finally {
        setViewAsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [viewAsSearch]);

  const handleEnterViewAs = async (targetUser) => {
    await enterViewAs(targetUser);
    navigate("/dashboard");
  };
  const [apiUrl, setApiUrl] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const canvasARef = useRef(null);
  const canvasBRef = useRef(null);
  const canvasCRef = useRef(null);
  const canvasDRef = useRef(null);
  const cloudSvgRef = useRef(null);
  const [canvasReady, setCanvasReady] = useState(false);

  // Pro badge generator
  const proDRef = useRef(null);
  const proERef = useRef(null);
  const proPlusRef = useRef(null);
  const teamsRef = useRef(null);
  const crownSvgRef = useRef(null);
  const [proReady, setProReady] = useState(false);

  // --- Shared drawing helpers ---
  const drawBackground = (ctx, S) => {
    ctx.clearRect(0, 0, S, S);
  };

  const drawLogoCircle = (ctx, cx, cy, radius) => {
    // Gradient ring
    const ring = ctx.createConicGradient(0, cx, cy);
    ring.addColorStop(0, "#60A5FA");
    ring.addColorStop(0.33, "#22D3EE");
    ring.addColorStop(0.66, "#C084FC");
    ring.addColorStop(1, "#60A5FA");
    ctx.strokeStyle = ring;
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();
    // White fill
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 2, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawCloudIcon = (ctx, x, y, scale = 1) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.arc(-7, 2, 6, Math.PI, 0);
    ctx.arc(2, -3, 8, Math.PI + 0.3, -0.3);
    ctx.arc(11, 2, 6, Math.PI, 0);
    ctx.lineTo(17, 6);
    ctx.lineTo(-13, 6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  };

  const drawPill = (ctx, x, y, w, h) => {
    const r = h / 2;
    const g = ctx.createLinearGradient(x, y, x + w, y);
    g.addColorStop(0, "#60A5FA");
    g.addColorStop(0.5, "#22D3EE");
    g.addColorStop(1, "#A78BFA");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(x + r, y + h);
    ctx.arc(x + r, y + r, r, Math.PI / 2, -Math.PI / 2);
    ctx.closePath();
    ctx.fill();
  };

  // --- Draw all 3 variations ---
  const drawVariations = useCallback(() => {
    const S = 512;
    let loaded = 0;

    const onAllLoaded = () => {
      loaded++;
      if (loaded === 4) setCanvasReady(true);
    };

    // --- Variation A: Centered pill badge below ---
    const drawA = () => {
      const canvas = canvasARef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = S;
      canvas.height = S;
      const cx = S / 2, cy = S / 2 - 30, R = 218;

      drawBackground(ctx, S);
      drawLogoCircle(ctx, cx, cy, R);

      const logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.onload = () => {
        const ls = 340;
        ctx.drawImage(logo, cx - ls / 2, cy - ls / 2, ls, ls);
        // Pill badge
        const pw = 220, ph = 46, px = cx - pw / 2, py = cy + R - 10;
        drawPill(ctx, px, py, pw, ph);
        drawCloudIcon(ctx, px + 44, py + ph / 2 - 1, 1.3);
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "600 20px system-ui, -apple-system, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("CLOUD", cx + 24, py + ph / 2);
        onAllLoaded();
      };
      logo.src = "/logo-256.png";
    };

    // --- Variation B: Cloud badge bottom-right corner ---
    const drawB = () => {
      const canvas = canvasBRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = S;
      canvas.height = S;
      const cx = S / 2, cy = S / 2, R = 225;

      drawBackground(ctx, S);
      drawLogoCircle(ctx, cx, cy, R);

      // Prepare cloud icon SVG data URL from hidden ref
      const svgEl = cloudSvgRef.current?.querySelector("svg");
      let cloudSvgUrl = null;
      if (svgEl) {
        const svgStr = new XMLSerializer().serializeToString(svgEl);
        cloudSvgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgStr);
      }

      const logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.onload = () => {
        const ls = 355;
        ctx.drawImage(logo, cx - ls / 2, cy - ls / 2, ls, ls);
        // Cloud badge — bottom-right, overlapping into logo area
        const bx = cx + R - 55, by = cy + R - 55, br = 70;
        // Badge circle bg
        const bg = ctx.createLinearGradient(bx - br, by - br, bx + br, by + br);
        bg.addColorStop(0, "#60A5FA");
        bg.addColorStop(0.5, "#22D3EE");
        bg.addColorStop(1, "#A78BFA");
        ctx.fillStyle = bg;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fill();
        // White border
        ctx.strokeStyle = "#FFFFFF";
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.stroke();
        // Cloud icon from IoIosCloud
        if (cloudSvgUrl) {
          const cloudImg = new Image();
          cloudImg.onload = () => {
            ctx.drawImage(cloudImg, bx - 38, by - 38, 76, 76);
            onAllLoaded();
          };
          cloudImg.src = cloudSvgUrl;
        } else {
          drawCloudIcon(ctx, bx - 2, by - 2, 2.8);
          onAllLoaded();
        }
      };
      logo.src = "/logo-256.png";
    };

    // --- Variation C: Minimal, no ring, gradient text ---
    const drawC = () => {
      const canvas = canvasCRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = S;
      canvas.height = S;
      const cx = S / 2, cy = S / 2 - 30;

      drawBackground(ctx, S);

      // Soft white circle (no ring)
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(cx, cy, 215, 0, Math.PI * 2);
      ctx.fill();

      const logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.onload = () => {
        const ls = 340;
        ctx.drawImage(logo, cx - ls / 2, cy - ls / 2, ls, ls);
        // Gradient "Cloud" text below
        const ty = cy + 250;
        const tGrad = ctx.createLinearGradient(cx - 120, ty, cx + 120, ty);
        tGrad.addColorStop(0, "#60A5FA");
        tGrad.addColorStop(0.5, "#22D3EE");
        tGrad.addColorStop(1, "#A78BFA");
        ctx.fillStyle = tGrad;
        ctx.font = "600 60px system-ui, -apple-system, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Cloud", cx, ty);
        onAllLoaded();
      };
      logo.src = "/logo-256.png";
    };

    // --- Variation D: Tag-style badge with background ---
    const drawD = () => {
      const canvas = canvasDRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = S;
      canvas.height = S;
      const cx = S / 2, cy = S / 2 - 12, R = 218;

      drawBackground(ctx, S);
      drawLogoCircle(ctx, cx, cy, R);

      const logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.onload = () => {
        const ls = 340;
        ctx.drawImage(logo, cx - ls / 2, cy - ls / 2, ls, ls);

        // Tag badge — overlapping bottom of circle
        const tw = 280, th = 72, tr = th / 2;
        const tx = cx - tw / 2, ty = cy + R - 30;

        // White border behind tag
        const bPad = 5;
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath();
        ctx.roundRect(tx - bPad, ty - bPad, tw + bPad * 2, th + bPad * 2, tr + bPad);
        ctx.fill();

        // Tag gradient fill
        const tg = ctx.createLinearGradient(tx, ty, tx + tw, ty);
        tg.addColorStop(0, "#60A5FA");
        tg.addColorStop(0.5, "#22D3EE");
        tg.addColorStop(1, "#A78BFA");
        ctx.fillStyle = tg;
        ctx.beginPath();
        ctx.roundRect(tx, ty, tw, th, tr);
        ctx.fill();

        // "CLOUD" text
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "900 38px system-ui, -apple-system, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("CLOUD", cx, ty + th / 2);

        onAllLoaded();
      };
      logo.src = "/logo-256.png";
    };

    drawA();
    drawB();
    drawC();
    drawD();
  }, []);

  useEffect(() => {
    drawVariations();
  }, [drawVariations]);

  // --- Pro badge gradient (amber → yellow, matching the PRO account treatment) ---
  const proGradient = (ctx, x0, y0, x1, y1) => {
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    g.addColorStop(0, "#F59E0B"); // amber-500
    g.addColorStop(0.5, "#FBBF24"); // amber-400
    g.addColorStop(1, "#FACC15"); // yellow-400
    return g;
  };

  // --- Cloud brand gradient (blue → cyan → purple, the Cloud Platform palette) ---
  const cloudGradient = (ctx, x0, y0, x1, y1) => {
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    g.addColorStop(0, "#3B82F6"); // blue-500
    g.addColorStop(0.5, "#06B6D4"); // cyan-500
    g.addColorStop(1, "#A855F7"); // purple-500
    return g;
  };

  const crownSvgUrl = () => {
    const svgEl = crownSvgRef.current?.querySelector("svg");
    if (!svgEl) return null;
    const svgStr = new XMLSerializer().serializeToString(svgEl);
    return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgStr);
  };

  const drawProVariations = useCallback(() => {
    const S = 512;
    let loaded = 0;
    const onAllLoaded = () => {
      loaded++;
      if (loaded === 4) setProReady(true);
    };

    const drawCrown = (ctx, cx, cy, size, cb) => {
      const url = crownSvgUrl();
      if (!url) {
        cb?.();
        return;
      }
      const img = new Image();
      img.onload = () => {
        // Tint the crown black via offscreen source-in compositing
        const off = document.createElement("canvas");
        off.width = size;
        off.height = size;
        const octx = off.getContext("2d");
        octx.drawImage(img, 0, 0, size, size);
        octx.globalCompositeOperation = "source-in";
        octx.fillStyle = "#1A1A1A";
        octx.fillRect(0, 0, size, size);
        ctx.drawImage(off, cx - size / 2, cy - size / 2);
        cb?.();
      };
      img.src = url;
    };

    // --- Product logo with gold PRO crown badge in corner ---
    const drawProD = () => {
      const canvas = proDRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = S;
      canvas.height = S;
      ctx.clearRect(0, 0, S, S);
      const cx = S / 2, cy = S / 2, R = 215;

      // White disc with black outline
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#1A1A1A";
      ctx.lineWidth = 22;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.stroke();

      const logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.onload = () => {
        const ls = 320;
        ctx.drawImage(logo, cx - ls / 2, cy - ls / 2 - 10, ls, ls);

        // Gold crown badge, bottom-right, black outline
        const bx = cx + R - 50, by = cy + R - 50, br = 72;
        ctx.fillStyle = proGradient(ctx, bx - br, by - br, bx + br, by + br);
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#1A1A1A";
        ctx.lineWidth = 16;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.stroke();

        drawCrown(ctx, bx, by, 74, onAllLoaded);
      };
      logo.src = "/logo-256.png";
    };

    // --- Product logo with a gold wordmark pill (PRO / PRO+ / TEAMS) ---
    // `fill` may be a solid color string or a gradient(ctx, x0, y0, x1, y1) function
    const drawWordmark = (ref, label, fill = "#FACC15", textColor = "#1A1A1A") => {
      const canvas = ref.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = S;
      canvas.height = S;
      ctx.clearRect(0, 0, S, S);
      const cx = S / 2, cy = S / 2, R = 215;

      // White disc with black outline
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#1A1A1A";
      ctx.lineWidth = 22;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.stroke();

      const logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.onload = () => {
        const ls = 282;
        ctx.drawImage(logo, cx - ls / 2, cy - ls / 2 - 28, ls, ls);

        // Gradient pill, width fit to the label, overlapping the bottom of the disc
        const fontSize = 68;
        ctx.font = `900 ${fontSize}px system-ui, -apple-system, sans-serif`;
        const textW = ctx.measureText(label).width;
        const ph = 112, pr = ph / 2;
        const pw = Math.max(ph + 60, textW + 88);
        const px = cx - pw / 2, py = cy + R - 86;

        // Pill
        ctx.fillStyle = typeof fill === "function" ? fill(ctx, px, py, px + pw, py) : fill;
        ctx.beginPath();
        ctx.roundRect(px, py, pw, ph, pr);
        ctx.fill();

        // Wordmark
        ctx.fillStyle = textColor;
        ctx.font = `900 ${fontSize}px system-ui, -apple-system, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, cx, py + ph / 2 + 1);

        onAllLoaded();
      };
      logo.src = "/logo-256.png";
    };

    drawProD();
    drawWordmark(proERef, "PRO");
    drawWordmark(proPlusRef, "PRO+");
    drawWordmark(teamsRef, "TEAMS", cloudGradient, "#FFFFFF");
  }, []);

  useEffect(() => {
    drawProVariations();
  }, [drawProVariations]);

  const handleExportPng = (canvasRef, filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = 543;
    exportCanvas.height = 543;
    const ctx = exportCanvas.getContext("2d");
    ctx.drawImage(canvas, 0, 0, 543, 543);
    const link = document.createElement("a");
    link.download = filename;
    link.href = exportCanvas.toDataURL("image/png");
    link.click();
  };

  const predefinedUrls = [
    { label: "Localhost", value: "http://localhost:3000", showUrl: true },
    { label: "Cloud Backend", value: "https://cloud-api.ultimatewebscraper.com", showUrl: true },
    { label: "Custom URL", value: "custom", showUrl: false },
  ];

  useEffect(() => {
    // Load current API URL
    const currentUrl = cloudApi.getApiUrl();
    setApiUrl(currentUrl);

    // Check if it's a custom URL
    const isPredefined = predefinedUrls.some((preset) => preset.value === currentUrl);
    if (!isPredefined) {
      setShowCustomInput(true);
      setCustomUrl(currentUrl);
      setApiUrl("custom");
    }

  }, []);

  const handleUrlChange = (e) => {
    const selectedValue = e.target.value;
    setApiUrl(selectedValue);

    if (selectedValue === "custom") {
      setShowCustomInput(true);
    } else {
      setShowCustomInput(false);
      setCustomUrl("");
    }
  };

  const handleSave = async () => {
    try {
      let urlToSave = apiUrl;

      if (apiUrl === "custom") {
        if (!customUrl) {
          setMessage({ type: "error", text: "Please enter a custom URL" });
          return;
        }
        urlToSave = customUrl;
      }

      // Validate URL format
      try {
        new URL(urlToSave);
      } catch (err) {
        setMessage({ type: "error", text: "Invalid URL format" });
        return;
      }

      // Logout user before changing API URL (token won't be valid on new backend)
      await logout();

      // Update the API URL
      await cloudApi.setApiUrl(urlToSave);

      setMessage({ type: "success", text: `API URL updated to: ${urlToSave}. Please log in again.` });

      // Reload page after a short delay to show login screen
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setMessage({ type: "error", text: `Failed to update API URL: ${error.message}` });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="pb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight">Admin Settings</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configure system settings and test features</p>
      </div>

      {/* View As User */}
      <div className="border border-orange-400/20 dark:border-orange-500/25 rounded-xl overflow-hidden bg-white dark:bg-gray-950 shadow-sm dark:shadow-[0_0_60px_rgba(249,115,22,0.10)]">
        {/* Card header */}
        <div className="px-6 py-4 border-b border-orange-400/15 dark:border-orange-500/20 bg-orange-50/80 dark:bg-transparent dark:bg-gradient-to-r dark:from-orange-500/[0.12] dark:to-transparent flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-500/15 border border-orange-300/60 dark:border-orange-500/25 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-orange-500 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">View As User</h3>
                <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-300/60 dark:border-orange-500/20">Debug</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Inspect the dashboard as any user</p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Active session pill */}
          {viewAsUser && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 dark:bg-orange-500/[0.13] border border-orange-200 dark:border-orange-500/30">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                </span>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center flex-shrink-0 ring-1 ring-orange-400/30">
                  <span className="text-[10px] font-bold text-white leading-none">
                    {(viewAsUser.name || viewAsUser.email).split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-semibold text-orange-700 dark:text-orange-300">{viewAsUser.name || viewAsUser.email}</div>
                  {viewAsUser.name && <div className="text-[11px] text-gray-500">{viewAsUser.email}</div>}
                </div>
              </div>
              <button
                onClick={exitViewAs}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-orange-600 dark:text-orange-400 border border-orange-300 dark:border-orange-500/30 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-500/10 transition-all"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Exit
              </button>
            </div>
          )}

          {/* Search input */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={viewAsSearch}
              onChange={(e) => setViewAsSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-9 pr-8 py-2.5 bg-gray-50 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.12] rounded-lg text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:border-orange-400 dark:focus:border-orange-500/60 focus:ring-1 focus:ring-orange-400/30 dark:focus:ring-orange-500/25 transition-all"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {viewAsSearching ? (
                <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : viewAsSearch.length > 0 ? (
                <button
                  onClick={() => setViewAsSearch('')}
                  className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 dark:bg-white/[0.15] hover:bg-gray-400 dark:hover:bg-white/[0.25] transition-all"
                >
                  <svg className="w-2.5 h-2.5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              ) : null}
            </div>
          </div>

          {/* Search results — always rendered, animated via max-height */}
          <div
            className="overflow-hidden transition-all duration-200 ease-in-out"
            style={{
              maxHeight: viewAsResults.length > 0
                ? `${viewAsResults.length * 56 + 8}px`
                : (viewAsSearch.length >= 2 && !viewAsSearching) ? '80px' : '0px',
            }}
          >
            {viewAsResults.length > 0 ? (
              <div className="space-y-1">
                {viewAsResults.map((u) => {
                  const initials = (u.name || u.email).split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                  const colors = ['from-blue-400 to-cyan-400', 'from-purple-400 to-pink-400', 'from-emerald-400 to-teal-400', 'from-orange-400 to-amber-400'];
                  const color = colors[(u.email.charCodeAt(0)) % colors.length];
                  return (
                    <div
                      key={u.id}
                      className="group flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] hover:bg-gray-100 dark:hover:bg-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.15] transition-all duration-150"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0 ring-1 ring-white/10`}>
                          <span className="text-[10px] font-bold text-white leading-none">{initials}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{u.name || u.email}</div>
                          {u.name && <div className="text-xs text-gray-500 truncate">{u.email}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                        {u.role === 'admin' && (
                          <span className="text-[10px] font-semibold tracking-wide px-1.5 py-0.5 bg-purple-100 dark:bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/20 rounded">ADMIN</span>
                        )}
                        {u.status !== 'active' && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 rounded">{u.status.toUpperCase()}</span>
                        )}
                        <button
                          onClick={() => handleEnterViewAs(u)}
                          className="px-3 py-1.5 text-xs font-semibold bg-orange-100 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-300 dark:border-orange-500/25 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-500/30 group-hover:border-orange-400/50 dark:group-hover:border-orange-400/40 transition-all duration-150"
                        >
                          View As →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1.5 py-4 text-center">
                <svg className="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-sm text-gray-400 dark:text-gray-500">No users found for <span className="font-medium text-gray-500 dark:text-gray-400">"{viewAsSearch}"</span></p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* API Server Configuration */}
      <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800/50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
              <span className="text-xl">🌐</span>
              API Server Configuration
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-500 mt-1">Change the backend API server URL for testing</p>
          </div>
        </div>

        {/* Message Display */}
        {message.text && (
          <div
            className={`mb-4 p-3 rounded-lg border ${
              message.type === "success"
                ? "bg-green-500/10 border-green-500/30 text-green-400"
                : message.type === "error"
                ? "bg-red-500/10 border-red-500/30 text-red-400"
                : "bg-blue-500/10 border-blue-500/30 text-blue-400"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Current API URL Display */}
        <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700/30">
          <div className="text-xs text-gray-600 dark:text-gray-500 mb-1">Current API URL:</div>
          <div className="text-sm text-gray-700 dark:text-gray-300 font-mono">{cloudApi.getApiUrl()}</div>
        </div>

        {/* URL Selection */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Select API Server</label>
            <div className="relative">
              <select
                value={apiUrl}
                onChange={handleUrlChange}
                className="appearance-none w-full px-4 pr-9 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 focus:outline-none focus:border-[#FFE100] focus:ring-1 focus:ring-[#FFE100]"
              >
                <option value="">Select a server...</option>
                {predefinedUrls.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                    {preset.showUrl ? ` (${preset.value})` : ""}
                  </option>
                ))}
              </select>
              <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Custom URL Input */}
          {showCustomInput && (
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Custom API URL</label>
              <input
                type="text"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://api.example.com"
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 focus:outline-none focus:border-[#FFE100] focus:ring-1 focus:ring-[#FFE100]"
              />
              <p className="text-xs text-gray-600 dark:text-gray-500 mt-1">Enter the full URL including protocol (http:// or https://)</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-[#FFE100] to-[#FFAA00] hover:from-[#FFAA00] hover:to-[#FFE100] text-black font-semibold rounded-lg transition-all shadow-lg shadow-[#FFE100]/20 hover:shadow-[#FFE100]/40"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Checkout Image Generator */}
      <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800/50 rounded-lg p-6">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <span className="text-xl">🎨</span>
            Checkout Image Generator
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-500 mt-1">Generate product images for Stripe checkout &middot; 543 x 543px RGB</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { ref: canvasARef, label: "A — Centered Badge", file: "checkout-centered.png" },
            { ref: canvasBRef, label: "B — Corner Cloud", file: "checkout-corner.png" },
            { ref: canvasCRef, label: "C — Minimal", file: "checkout-minimal.png" },
            { ref: canvasDRef, label: "D — Cloud Tag", file: "checkout-tag.png" },
          ].map(({ ref, label, file }) => (
            <div key={label} className="flex flex-col items-center gap-2">
              <div className="p-2 rounded-lg border border-gray-200 dark:border-gray-700/30" style={{ backgroundImage: "linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)", backgroundSize: "16px 16px", backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px" }}>
                <canvas ref={ref} className="rounded" style={{ width: 180, height: 180 }} />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-500 font-medium">{label}</span>
              <button
                onClick={() => handleExportPng(ref, file)}
                disabled={!canvasReady}
                className="px-3 py-1.5 text-xs bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 disabled:opacity-50 text-white font-semibold rounded-lg transition-all flex items-center gap-1.5"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                PNG
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Pro Badge Generator */}
      <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800/50 rounded-lg p-6">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
            <FaCrown className="text-yellow-500" />
            Pro Badge Generator
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-500 mt-1">Generate Pro user badges &middot; product logo with gold crown or wordmark &middot; 543 x 543px RGB</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { ref: proDRef, label: "Crown badge", file: "pro-logo-crown.png" },
            { ref: proERef, label: "PRO wordmark", file: "pro-logo-wordmark.png" },
            { ref: proPlusRef, label: "PRO+ wordmark", file: "pro-plus-logo-wordmark.png" },
            { ref: teamsRef, label: "TEAMS wordmark", file: "teams-logo-wordmark.png" },
          ].map(({ ref, label, file }) => (
            <div key={label} className="flex flex-col items-center gap-2">
              <div className="p-2 rounded-lg border border-gray-200 dark:border-gray-700/30" style={{ backgroundImage: "linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)", backgroundSize: "16px 16px", backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px" }}>
                <canvas ref={ref} className="rounded" style={{ width: 200, height: 200 }} />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-500 font-medium">{label}</span>
              <button
                onClick={() => handleExportPng(ref, file)}
                disabled={!proReady}
                className="px-4 py-1.5 text-xs bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-600 hover:to-yellow-500 disabled:opacity-50 text-white font-semibold rounded-lg transition-all flex items-center gap-1.5"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                PNG
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* System Info */}
      <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2 mb-4">
          <span className="text-xl">ℹ️</span>
          System Information
        </h3>
        <div className="text-sm">
          <div>
            <div className="text-gray-600 dark:text-gray-500">Environment</div>
            <div className="text-gray-700 dark:text-gray-300">{process.env.NODE_ENV || "development"}</div>
          </div>
        </div>
      </div>

      {/* Hidden IoIosCloud icon for canvas rendering */}
      <div ref={cloudSvgRef} style={{ position: "absolute", left: -9999, top: -9999 }}>
        <IoIosCloud size={60} color="#FFFFFF" />
      </div>

      {/* Hidden FaCrown icon for pro badge canvas rendering */}
      <div ref={crownSvgRef} style={{ position: "absolute", left: -9999, top: -9999 }}>
        <FaCrown size={60} color="#FFFFFF" />
      </div>
    </div>
  );
};

export default AdminView;
